<template>
  <div class="mb-4">
    <label class="form-label">{{ $t('campaigns.facebook.location_targeting') }}</label>

    <div v-if="availableCountries.length > 0">
      <FacebookTargetingElement
        v-for="(loc, index) in modelValue || []"
        :key="index"
        :value="loc"
        @change="(change) => updateLocation(change, index as number)"
        @remove="removeLocation(index as number)"
        :campaignId="props.campaignId"
        :availableCountries="availableCountries" />

      <FormErrorList :errors="flattenedErrors" />

      <LeadButton class="btn mt-1" @click="createPlaceTargeting" variant="secondary">
        <template #prepend-icon>
          <IconLocation size="16"></IconLocation>
        </template>
        {{ $t('campaigns.facebook.add_location') }}
      </LeadButton>
    </div>
    <div v-else class="p-3">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">{{ $t('campaigns.facebook.loading') }}</span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { flatErrors } from '@/helpers/helpers';
import { FormErrorList } from '@prospective/lithium';
import FacebookTargetingElement from '@/components/form/FacebookTargetingElement.vue';
import type { IFacebookCountry, IFacebookTargeting } from '@/types/locationTargeting';
import { useToast } from 'vue-toastification';
import { getAvailableCountries } from '@/api/campaign';
import LeadButton from '../generic/LeadButton.vue';
import { IconLocation } from '@tabler/icons-vue';
import translator from '@/locale/translator';

const toast = useToast();
/* ---------------------------------- PROPS --------------------------------- */
const modelValue = defineModel<any>({ required: true });
const props = withDefaults(
  defineProps<{
    value?: { target_locations?: IFacebookTargeting[] };
    campaignId: number;
    errors: any;
  }>(),
  {
    value: () => {
      return {};
    },
  }
);

/* -------------------------------- STATE -------------------------------- */

const availableCountries = ref<IFacebookCountry[]>([]);

/* -------------------------------- COMPUTED -------------------------------- */

const flattenedErrors = computed(() => flatErrors(props.errors));

/* ---------------------------------- LIFECYLCE --------------------------------- */

onMounted(() => {
  fetchAvailableCountries();
});

/* --------------------------------- METHODS -------------------------------- */
const fetchAvailableCountries = async () => {
  await getAvailableCountries(props.campaignId.toString(), '')
    .then((response: { data: IFacebookCountry[] }) => {
      availableCountries.value = response.data;
    })
    .catch(() => {
      toast.error(translator.get('campaigns.facebook.failed_retrieving_targeting_data'));
    });
};

function createPlaceTargeting() {
  const newLocation: Partial<IFacebookTargeting> = {
    target_country: undefined,
    target_label: undefined,
    target_name: undefined,
    target_key: undefined,
    target_type: undefined,
    target_radius: undefined,
    target_radius_metric_unit: 'kilometer',
  };

  modelValue.value = [...(modelValue.value || []), newLocation];
}

function updateLocation(changedLocation: Partial<IFacebookTargeting>, index: number) {
  const updatedLocations = [
    ...(modelValue.value?.slice(0, index) || []),
    changedLocation,
    ...(modelValue.value?.slice(index + 1) || []),
  ];
  modelValue.value = updatedLocations;
}

function removeLocation(indexToRemove: number) {
  modelValue.value = modelValue.value?.filter((_: null, index: number) => index !== indexToRemove) || [];
}
</script>

<style scoped>
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}
</style>
